import type { SkillId } from "./skills.js";

export const ITEM_IDS = [
  // Digging gathers
  "pebble",
  "bone_shard",
  "iron_nugget",
  "charcoal",
  "silver_tag",
  "gold_medallion",
  "diamond_chew",
  "nova_bone",
  // Fetching gathers
  "twig",
  "stick",
  "hardwood_stick",
  "maple_branch",
  "enchanted_stick",
  // Foraging gathers (Sniffing)
  "fallen_crumb",
  "windfall_berry",
  "park_apple",
  "cheese_rind",
  "picnic_scrap",
  "gourmet_nibble",
  "royal_truffle",
  // Begging handouts
  "handout_crumb",
  "soft_biscuit",
  "pretzel_piece",
  "cafe_scone",
  "park_hotdog",
  "fancy_croissant",
  "steak_tip",
  // Treats
  "bronze_treat",
  "iron_treat",
  "steel_treat",
  "silver_treat",
  "gold_treat",
  "nova_treat",
  // Dig tools
  "pup_shovel",
  "iron_shovel",
  "steel_shovel",
  "silver_shovel",
  "gold_shovel",
  "diamond_shovel",
  "nova_shovel",
  // Fetch toys
  "rope_ball",
  "iron_ball",
  "steel_ball",
  "silver_ball",
  "gold_ball",
  "diamond_ball",
  "nova_ball",
  // Boosts + social
  "elixir_of_the_deep_mint",
  "scroll_of_timber_surge",
  "snack_sense_tonic",
  "puppy_eyes_elixir",
  "volcanic_forgemaster_draught",
  "scroll_of_hospitality",
  // Legacy cosmetic (no longer worn)
  "collar",
] as const;

export type ItemId = (typeof ITEM_IDS)[number];

export type ToolKind = "dig" | "fetch";

export interface ItemDefinition {
  id: ItemId;
  name: string;
  description: string;
  stackable: boolean;
  color: number;
  toolKind?: ToolKind;
  /** 0 = starter, 6 = nova. */
  toolTier?: number;
  boostSkill?: SkillId;
  consumable?: boolean;
}

function item(
  id: ItemId,
  name: string,
  description: string,
  color: number,
  extra: Partial<ItemDefinition> = {},
): ItemDefinition {
  return { id, name, description, stackable: true, color, ...extra };
}

function tool(
  id: ItemId,
  name: string,
  description: string,
  color: number,
  kind: ToolKind,
  tier: number,
): ItemDefinition {
  return {
    id,
    name,
    description,
    stackable: false,
    color,
    toolKind: kind,
    toolTier: tier,
  };
}

export const ITEMS: Record<ItemId, ItemDefinition> = {
  pebble: item("pebble", "Pebble", "A little stone.", 0x90a4ae),
  bone_shard: item("bone_shard", "Bone", "Crunchy and buried.", 0xfff8e1),
  iron_nugget: item("iron_nugget", "Iron", "Heavy park metal.", 0xb0bec5),
  charcoal: item("charcoal", "Charcoal", "Smoky and useful.", 0x455a64),
  silver_tag: item("silver_tag", "Silver", "A shiny tag.", 0xeceff1),
  gold_medallion: item("gold_medallion", "Gold", "Best in show.", 0xffd54f),
  diamond_chew: item("diamond_chew", "Diamond", "A sparkling chew.", 0xb3e5fc),
  nova_bone: item("nova_bone", "Nova", "A glowing bone.", 0xce93d8),

  twig: item("twig", "Twig", "A starter stick.", 0xa1887f),
  stick: item("stick", "Stick", "A good stick.", 0x8d6e63),
  hardwood_stick: item("hardwood_stick", "Hardwood", "Tough fetch wood.", 0x6d4c41),
  maple_branch: item("maple_branch", "Maple", "Sweet and sturdy.", 0xd84315),
  enchanted_stick: item("enchanted_stick", "Magic Stick", "It wags back.", 0x7e57c2),

  fallen_crumb: item("fallen_crumb", "Crumb", "Dropped snack scrap.", 0xffe0b2),
  windfall_berry: item("windfall_berry", "Berry", "Still juicy.", 0xe91e63),
  park_apple: item("park_apple", "Apple", "A park apple.", 0xef5350),
  cheese_rind: item("cheese_rind", "Cheese", "The good end.", 0xffe082),
  picnic_scrap: item("picnic_scrap", "Scrap", "Picnic leftovers.", 0xffcc80),
  gourmet_nibble: item("gourmet_nibble", "Nibble", "Fancy leftover.", 0xff8a65),
  royal_truffle: item("royal_truffle", "Truffle", "Nose-found treasure.", 0x6d4c41),

  handout_crumb: item("handout_crumb", "Handout", "Won with puppy eyes.", 0xffe0b2),
  soft_biscuit: item("soft_biscuit", "Biscuit", "A soft park treat.", 0xffcc80),
  pretzel_piece: item("pretzel_piece", "Pretzel", "Salty and twisted.", 0xd7ccc8),
  cafe_scone: item("cafe_scone", "Scone", "A cafe handout.", 0xffe082),
  park_hotdog: item("park_hotdog", "Hotdog", "The classic bribe.", 0xef9a9a),
  fancy_croissant: item("fancy_croissant", "Croissant", "Flaky and earned.", 0xffe0b2),
  steak_tip: item("steak_tip", "Steak", "The big reward.", 0xc62828),

  bronze_treat: item("bronze_treat", "Bronze Treat", "A beginner biscuit.", 0xcd7f32),
  iron_treat: item("iron_treat", "Iron Treat", "A sturdy snack.", 0x9e9e9e),
  steel_treat: item("steel_treat", "Steel Treat", "A tough snack.", 0x78909c),
  silver_treat: item("silver_treat", "Silver Treat", "A shiny snack.", 0xf5f5f5),
  gold_treat: item("gold_treat", "Gold Treat", "A fancy snack.", 0xffc107),
  nova_treat: item("nova_treat", "Nova Treat", "A starry snack.", 0xab47bc),

  pup_shovel: tool("pup_shovel", "Pup Shovel", "Your first digger.", 0x8d6e63, "dig", 0),
  iron_shovel: tool("iron_shovel", "Iron Shovel", "A solid digger.", 0x90a4ae, "dig", 1),
  steel_shovel: tool("steel_shovel", "Steel Shovel", "A sharp digger.", 0x78909c, "dig", 2),
  silver_shovel: tool("silver_shovel", "Silver Shovel", "A shiny digger.", 0xeceff1, "dig", 3),
  gold_shovel: tool("gold_shovel", "Gold Shovel", "A fancy digger.", 0xffd54f, "dig", 4),
  diamond_shovel: tool("diamond_shovel", "Diamond Shovel", "A sparkling digger.", 0xb3e5fc, "dig", 5),
  nova_shovel: tool("nova_shovel", "Nova Shovel", "The best digger.", 0xce93d8, "dig", 6),

  rope_ball: tool("rope_ball", "Rope Ball", "A knotted toy.", 0xa1887f, "fetch", 0),
  iron_ball: tool("iron_ball", "Iron Ball", "A heavy toy.", 0x90a4ae, "fetch", 1),
  steel_ball: tool("steel_ball", "Steel Ball", "A bouncy toy.", 0x78909c, "fetch", 2),
  silver_ball: tool("silver_ball", "Silver Ball", "A shiny toy.", 0xeceff1, "fetch", 3),
  gold_ball: tool("gold_ball", "Gold Ball", "A fancy toy.", 0xffd54f, "fetch", 4),
  diamond_ball: tool("diamond_ball", "Diamond Ball", "A sparkling toy.", 0xb3e5fc, "fetch", 5),
  nova_ball: tool("nova_ball", "Nova Ball", "The best toy.", 0xce93d8, "fetch", 6),

  elixir_of_the_deep_mint: {
    id: "elixir_of_the_deep_mint",
    name: "Dig Elixir",
    description: "50% faster Digging for 5 minutes.",
    stackable: true,
    color: 0x26c6da,
    boostSkill: "digging",
    consumable: true,
  },
  scroll_of_timber_surge: {
    id: "scroll_of_timber_surge",
    name: "Fetch Scroll",
    description: "50% faster Fetching for 5 minutes.",
    stackable: true,
    color: 0x9ccc65,
    boostSkill: "fetching",
    consumable: true,
  },
  snack_sense_tonic: {
    id: "snack_sense_tonic",
    name: "Sniff Tonic",
    description: "50% faster Sniffing for 5 minutes.",
    stackable: true,
    color: 0xf48fb1,
    boostSkill: "sniffing",
    consumable: true,
  },
  puppy_eyes_elixir: {
    id: "puppy_eyes_elixir",
    name: "Beg Elixir",
    description: "50% faster Begging for 5 minutes.",
    stackable: true,
    color: 0xffab91,
    boostSkill: "begging",
    consumable: true,
  },
  volcanic_forgemaster_draught: {
    id: "volcanic_forgemaster_draught",
    name: "Craft Draught",
    description: "50% faster Toycraft for 5 minutes.",
    stackable: true,
    color: 0xff7043,
    boostSkill: "toycraft",
    consumable: true,
  },
  scroll_of_hospitality: {
    id: "scroll_of_hospitality",
    name: "Home Scroll",
    description: "Place a 60-second ring friends can use to visit your kennel.",
    stackable: true,
    color: 0xffcc80,
    consumable: true,
  },
  collar: {
    id: "collar",
    name: "Collar",
    description: "A handsome collar.",
    stackable: false,
    color: 0x5d4037,
  },
};

export interface ItemStack {
  itemId: ItemId;
  quantity: number;
}

export type InventorySlot = ItemStack | null;

/** @deprecated Kept for loading old saves that still stored worn slots. */
export interface EquipmentState {
  collar: ItemId | null;
  digTool: ItemId | null;
  fetchToy: ItemId | null;
}

export function emptyEquipment(): EquipmentState {
  return { collar: null, digTool: null, fetchToy: null };
}

/** Highest-tier dig/fetch tool sitting in the pack (no equipping). */
export function bestToolInPack(inventory: readonly InventorySlot[], kind: ToolKind): ItemId | null {
  let best: ItemId | null = null;
  let bestTier = -1;
  for (const slot of inventory) {
    if (!slot) {
      continue;
    }
    const def = ITEMS[slot.itemId];
    if (def.toolKind !== kind) {
      continue;
    }
    const tier = def.toolTier ?? 0;
    if (tier > bestTier) {
      best = slot.itemId;
      bestTier = tier;
    }
  }
  return best;
}

/** Tool speed: starter 1.0 → best 0.80. */
export function toolSpeedMultiplier(tier: number | undefined): number {
  if (tier === undefined) {
    return 1;
  }
  return 1 - (tier / 6) * 0.2;
}

/** Tool XP: starter 1.0 → best 1.12. */
export function toolXpMultiplier(tier: number | undefined): number {
  if (tier === undefined) {
    return 1;
  }
  return 1 + (tier / 6) * 0.12;
}

export function isItemId(value: string): value is ItemId {
  return Object.prototype.hasOwnProperty.call(ITEMS, value);
}
